import { AfterViewInit, Component, ElementRef, Inject, LOCALE_ID, QueryList, ViewChildren } from '@angular/core';
import { HeaderModule } from "../header/header.module";
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService, submitOrgKeyAPI } from '../api/api.service';
import { ApiAboutService } from '../Services/api-about.service';
import { FeatureSetResponse } from '../backend-types/feature-set';
import { DialogService, DialogType } from '../Services/dialog.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { dashboardRoute, verifyWithOrgKeyRoute } from '../route-names';
import { Router } from '@angular/router';
import { ElectorDataService } from '../Services/elector-data.service';
import { BackButtonConfirmationModalModule } from '../back-button-confirmation-modal/back-button-confirmation-modal.module';

@Component({
    selector: 'app-orgkey-manual',
    imports: [HeaderModule, MatProgressSpinnerModule, BackButtonConfirmationModalModule],
    templateUrl: './orgkey-manual.component.html',
    styleUrl: './orgkey-manual.component.scss'
})
export class OrgkeyManualComponent implements AfterViewInit {
  @ViewChildren('orgKeyRef') orgKeyInputFields!: QueryList<ElementRef>;
  numbers = [0, 1, 2, 3, 4, 5];
  orgKeyFormGroup: FormGroup;
  displaySpinner: boolean = false;
  dialogTitle: string = "";
  dialogMessage: string = "";

  constructor(
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private aboutService: ApiAboutService,
    private dialogService: DialogService,
    private router: Router,
    readonly userService: ElectorDataService,
    @Inject(LOCALE_ID) readonly locale: string
  ) {
      this.orgKeyFormGroup = this.formBuilder.group({
        code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
      });    
  }

  ngAfterViewInit() {
    if(!this.userService.isOrgVerificationAllowed){
      this.dialogMessage = $localize`You have no organization verification attempts remaining.<br><br>You may attempt to verify with an organization again in ${this.userService.orgVerifyLockoutTimeRemainingString()}.`;
      this.dialogTitle = $localize`Please Wait.`;
      this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
      this.router.navigate(dashboardRoute);
      return;
    }
  }

  handleInput(event: Event, index: number): void {
    const inputElement = event.target as HTMLInputElement;
    const value = this.validateNumericInput(inputElement.value.slice(0, 1));
    inputElement.value = value;

    if (!value) {
      this.updateFormControl(); 
      return;
    }

    const nextInput = document.querySelectorAll<HTMLInputElement>('#otp > input')[index + 1];
    if (index === 5) {
      inputElement.select(); 
    } else if (nextInput) {
      nextInput.focus(); 
    }

    this.updateFormControl();
  }

  handleFocus(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.select(); 
  }

  handleBlur(event: FocusEvent): void {
    const inputElement = event.target as HTMLInputElement;
    inputElement.value = this.validateNumericInput(inputElement.value);
    this.updateFormControl();
  }

  handlePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipboardData = event.clipboardData?.getData('text') || '';
    const pastedValues = clipboardData.split('').filter((char) => /^\d$/.test(char)); 

    if (pastedValues.length !== 6) {
      this.orgKeyFormGroup.get('code')?.setErrors({ invalidPaste: true });
      return;
    }

    const inputs = document.querySelectorAll<HTMLInputElement>('#otp > input');
    inputs.forEach((input, i) => {
      input.value = pastedValues[i] || ''; 
    });

    inputs[inputs.length - 1]?.focus(); 
    this.updateFormControl();
  }

  updateFormControl(): void {
    const inputs = document.querySelectorAll<HTMLInputElement>('#otp > input');
    const code = Array.from(inputs)
      .map((input) => input.value)
      .join('');

    const control = this.orgKeyFormGroup.get('code');
    control?.setValue(code);

    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      control?.setErrors({ invalidCode: true });
    } else {
      control?.setErrors(null);
    }
  }

  validateNumericInput(value: string): string {
    return /^\d$/.test(value) ? value : '';
  }

  isValidCode(value: string): boolean {
    return /^\d{6}$/.test(value);
  }

  submit() {
    if(!this.userService.isOrgVerificationAllowed){
      this.dialogMessage = $localize`You have no attempts remaining.<br><br>You may attempt to verify with an organization again in ${this.userService.orgVerifyLockoutTimeRemainingString()}.`;
      this.dialogTitle = $localize`Please Wait.`;
      this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
      this.router.navigate(dashboardRoute);
      return;
    }
    if(!this.isValidCode(this.orgKeyFormGroup.value.code)) {
      this.resetOrgKeyInput();
      this.dialogMessage = $localize`Please enter 6 digits.`
      this.proccessOrgVerifyRemainingAttempts();
      this.dialogTitle = $localize`Malformed Data`;
      this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogMessage);
      return;
    }
    this.displaySpinner = true;
    this.apiService.postWithHandshakeCheck<FeatureSetResponse>(submitOrgKeyAPI, {
      orgKey: +this.orgKeyFormGroup.value.code,
      locale: this.locale
    }).subscribe({
      next: (resp) => {
        this.displaySpinner = false;
        if(resp.body) {
          this.userService.resetOrgVerifyAttempts();
          this.aboutService.dashboardFlowFeatureSet = resp.body;
          this.aboutService.headerImageUrl.set("");
          this.aboutService.headerTitle.set(this.aboutService.dashboardFlowFeatureSet.miscSettings?.header ?? "");
          this.router.navigate(verifyWithOrgKeyRoute);
        } else {
          this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          this.proccessOrgVerifyRemainingAttempts();
          this.dialogTitle = $localize`Unknown Error`;
          this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
        }
      },
      error: (err) => {
        this.resetOrgKeyInput();
        this.displaySpinner = false;
        if ((err.error_type !== undefined && err.error_type === 'INVALID_IDENTITY') ||
          (err.error !== undefined && err.error.error_type === 'INVALID_IDENTITY')) {
          const title = $localize`Invalid Organization Verification Key`
          this.dialogMessage = $localize`The provided key does not belong to an organization that you are registered with. Please verify that you have entered the key correctly. If you believe this is in error, use the <b>"Contact Support"</b> link for assistance.`;
          this.proccessOrgVerifyRemainingAttempts();
          this.dialogService.openDialog(DialogType.ERROR, title, this.dialogMessage);
        } else if ((err.error_type !== undefined && err.error_type === 'INVALID_API') ||
        (err.error !== undefined && err.error.error_type === 'INVALID_API')) {
          this.dialogMessage = $localize`The API key is invalid. Please use the "Contact Support" link for assistance.`;
          this.proccessOrgVerifyRemainingAttempts();
          this.dialogTitle = $localize`Invalid API Key`;
          this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogMessage);
        } else if ((err.error_type !== undefined && err.error_type === 'INVALID_FORM') ||
        (err.error !== undefined && err.error.error_type === 'INVALID_FORM')) {
          this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          this.proccessOrgVerifyRemainingAttempts();
          this.dialogTitle = $localize`Malformed Data`
          this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogMessage);
        } else if ((err.error_type !== undefined && err.error_type === 'VERIFY_ORG_LOCKED') ||
        (err.error !== undefined && err.error.error_type === 'VERIFY_ORG_LOCKED')) {
          this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          this.userService.orgVerifyAttemptsRemaining = 1;
          this.proccessOrgVerifyRemainingAttempts();
          this.dialogTitle = $localize`Too Many Attempts`;
          this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
        } else if ((err.error_type !== undefined && (err.error_type === 'DECODER_STATE_LOST' || err.error_type === 'INVALID_MTE_DECODING')) ||
        (err.error !== undefined && (err.error.error_type === 'DECODER_STATE_LOST' || err.error.error_type === 'INVALID_MTE_DECODING' ))) {
          this.dialogMessage = $localize`We could not process your request. Please log out, log in again, and try again.`;
          this.dialogTitle = $localize`Error.`
          this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogMessage);
        } else {
          this.dialogMessage = $localize`We could not process your request. Please use the "Contact Support" link for assistance.`;
          this.proccessOrgVerifyRemainingAttempts();
          this.dialogTitle = $localize`Unknown Error`;
          this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogMessage);
        }
      }
    })
  }

  back() {
    this.router.navigate(dashboardRoute)
  }

  proccessOrgVerifyRemainingAttempts() {
    this.userService.dereaseOrgVerifyAttemptsRemaining();
    if(this.userService.orgVerifyAttemptsRemaining === 1){
      this.dialogMessage += $localize`<br><br>You have 1 attempt remaining.`;
    } else if (this.userService.orgVerifyAttemptsRemaining === 0){
      this.dialogMessage += $localize`<br><br>You have no attempts remaining.<br><br>You may attempt to verify with an organization again in 15 minutes.`;
      this.userService.startOrgVerifyLockoutTimer();
      setTimeout(()=>{
        this.router.navigate(dashboardRoute);
      }, 3000);
    } else {
      this.dialogMessage += $localize`<br><br>You have ${this.userService.orgVerifyAttemptsRemaining} attempts remaining.`;
    }
  }

  resetOrgKeyInput() {
    this.orgKeyInputFields.forEach((input) => {
      input.nativeElement.value = '';
    });
    this.orgKeyFormGroup.get('code')?.reset();
    this.orgKeyFormGroup.reset();
  }

}
